import { supabase } from '../supabaseClient';

export interface Review {
  id: string;
  client_id: string;
  client_name: string;
  client_avatar: string | null;
  rating: number;
  comment: string;
  project_title?: string;
  created_at: string;
}

export interface ProfileData {
  id: string;
  role: 'CLIENT' | 'FREELANCER' | 'AGENCY' | 'ADMIN';
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  full_name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  company_bulstat: string | null;
  bio: string | null;
  skills: string[];
  portfolio_url: string | null;
  website_url: string | null;
  github_url: string | null;
  linkedin_url: string | null;
  location: string | null;
  timezone: string;
  avatar_url: string | null;
  cover_image_url: string | null;
  verified: boolean;
  verification_documents: any;
  hourly_rate: number | null;
  rating_avg: number;
  rating_cnt: number;
  completed_jobs: number;
  total_earnings: number;
  reviews: Review[];
  experience: Array<{
    id: string;
    company: string;
    position: string;
    startDate: string;
    endDate: string;
    description: string;
    current: boolean;
  }>;
  education: Array<{
    id: string;
    institution: string;
    degree: string;
    field: string;
    startDate: string;
    endDate: string;
    description: string;
  }>;
  portfolio: Array<{
    id: string;
    title: string;
    description: string;
    image_url: string | null;
    project_url: string | null;
    github_url: string | null;
    technologies: string[];
    completed_date: string;
    featured: boolean;
  }>;
  created_at: string;
  updated_at: string;
}

export interface UpdateProfileData {
  full_name?: string;
  email?: string;
  phone?: string;
  company?: string;
  company_bulstat?: string;
  bio?: string;
  skills?: string[];
  portfolio_url?: string;
  website_url?: string;
  github_url?: string;
  linkedin_url?: string;
  location?: string;
  timezone?: string;
  avatar_url?: string;
  cover_image_url?: string;
  hourly_rate?: number;
  experience?: Array<{
    id: string;
    company: string;
    position: string;
    startDate: string;
    endDate: string;
    description: string;
    current: boolean;
  }>;
  education?: Array<{
    id: string;
    institution: string;
    degree: string;
    field: string;
    startDate: string;
    endDate: string;
    description: string;
  }>;
  portfolio?: Array<{
    id: string;
    title: string;
    description: string;
    image_url: string | null;
    project_url: string | null;
    github_url: string | null;
    technologies: string[];
    completed_date: string;
    featured: boolean;
  }>;
}

// Get current user's profile
export const getCurrentUserProfile = async (): Promise<ProfileData | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    console.log('Getting profile for user:', user.id, user.email);

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      if (error.code === 'PGRST116') {
        // Profile doesn't exist, create a default one
        console.log('Profile not found, creating default profile...');
        return await createDefaultProfile(user);
      }
      throw error;
    }

    console.log('Profile found:', data);
    return data;
  } catch (error) {
    console.error('Error in getCurrentUserProfile:', error);
    throw error;
  }
};

// Create default profile for new user
const createDefaultProfile = async (user: any): Promise<ProfileData | null> => {
  try {
    const defaultProfile = {
      id: user.id,
      role: 'FREELANCER' as const,
      status: 'ACTIVE' as const,
      full_name: user.email?.split('@')[0] || 'User',
      email: user.email,
      phone: null,
      company: null,
      company_bulstat: null,
      bio: null,
      skills: [],
      portfolio_url: null,
      website_url: null,
      github_url: null,
      linkedin_url: null,
      location: null,
      timezone: 'Europe/Sofia',
      avatar_url: null,
      cover_image_url: null,
      verified: false,
      verification_documents: null,
      hourly_rate: null,
      rating_avg: 0,
      rating_cnt: 0,
      completed_jobs: 0,
      total_earnings: 0,
      reviews: [],
      experience: [],
      education: [],
      portfolio: []
    };

    const { data, error } = await supabase
      .from('profiles')
      .insert([defaultProfile])
      .select()
      .single();

    if (error) {
      console.error('Error creating default profile:', error);
      return null;
    }

    console.log('Default profile created:', data);
    return data;
  } catch (error) {
    console.error('Error in createDefaultProfile:', error);
    return null;
  }
};

// Update current user's profile
export const updateCurrentUserProfile = async (updates: UpdateProfileData): Promise<ProfileData> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating profile:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateCurrentUserProfile:', error);
    throw error;
  }
};

// Create profile for new user
export const createUserProfile = async (profileData: {
  id: string;
  role: 'CLIENT' | 'FREELANCER' | 'AGENCY' | 'ADMIN';
  full_name?: string;
  email?: string;
  phone?: string;
  company?: string;
  bio?: string;
  skills?: string[];
  location?: string;
}): Promise<ProfileData> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .insert([profileData])
      .select()
      .single();

    if (error) {
      console.error('Error creating profile:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in createUserProfile:', error);
    throw error;
  }
};

// Get profile by ID (for public viewing)
export const getProfileById = async (profileId: string): Promise<ProfileData | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', profileId)
      .single();

    if (error) {
      console.error('Error fetching profile by ID:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getProfileById:', error);
    throw error;
  }
};

// Upload avatar image
export const uploadAvatar = async (file: File, userId: string): Promise<string> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Error uploading avatar:', uploadError);
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error in uploadAvatar:', error);
    throw error;
  }
};

// Get reviews for a profile
export const getProfileReviews = async (profileId: string): Promise<Review[]> => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select(`
        id,
        client_id,
        rating,
        comment,
        project_title,
        created_at,
        profiles!reviews_client_id_fkey (
          full_name,
          avatar_url
        )
      `)
      .eq('freelancer_id', profileId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }

    return data?.map(review => ({
      id: review.id,
      client_id: review.client_id,
      client_name: (review.profiles as any)?.full_name || 'Анонимен клиент',
      client_avatar: (review.profiles as any)?.avatar_url || null,
      rating: review.rating,
      comment: review.comment,
      project_title: review.project_title,
      created_at: review.created_at,
    })) || [];
  } catch (error) {
    console.error('Error in getProfileReviews:', error);
    return [];
  }
};

// Get user's recent activity (projects, offers, etc.)
export const getUserRecentActivity = async (_userId: string) => {
  try {
    // For now, return mock data since projects and offers tables might not exist
    // In a real application, this would fetch actual data from the database
    return {
      projects: [
        {
          id: '1',
          title: 'Разработка на уебсайт',
          status: 'Завършен',
          created_at: '2023-10-20T10:00:00Z',
          updated_at: '2023-10-26T10:00:00Z'
        },
        {
          id: '2',
          title: 'Мобилно приложение',
          status: 'В процес',
          created_at: '2023-11-10T14:30:00Z',
          updated_at: '2023-11-15T14:30:00Z'
        },
        {
          id: '3',
          title: 'Дизайн на лого',
          status: 'Завършен',
          created_at: '2023-11-25T09:15:00Z',
          updated_at: '2023-11-30T09:15:00Z'
        }
      ],
      offers: [
        {
          id: '1',
          project_id: '4',
          status: 'Приета',
          created_at: '2023-11-28T11:00:00Z',
          updated_at: '2023-12-01T09:00:00Z',
          projects: { title: 'Дизайн на лого' }
        },
        {
          id: '2',
          project_id: '5',
          status: 'Очакваща',
          created_at: '2023-12-05T16:20:00Z',
          updated_at: '2023-12-05T16:20:00Z',
          projects: { title: 'E-commerce платформа' }
        }
      ],
    };
  } catch (error) {
    console.error('Error in getUserRecentActivity:', error);
    // Return empty data instead of throwing error
    return {
      projects: [],
      offers: [],
    };
  }
};

// Convert file to base64
export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};
